/**
 * The two orthogonal axes of a list (#1803, design §6.6).
 *
 *   SOURCE      — who owns the query state. Changes the component contract.
 *   CAPABILITY  — which affordances are offered. Opt-in one at a time, and it
 *                 does NOT change the component contract.
 *
 * Keeping them apart is the whole point. A view type per capability combination
 * ("list with search" / "list without search" / "list with search and export"…)
 * is a 2ⁿ explosion and was explicitly rejected; there is ONE `<ListView>` and
 * three source hooks feeding it.
 */
import type { FilterWhitelist, SortWhitelist } from './whitelist';

/**
 * Where a list's rows and query state come from.
 *
 *   remote — server-side paginate/filter/sort. State lives in the URL.
 *   bulk   — one capped fetch, everything else in memory. State lives in the
 *            URL too: it is a PAGE-LEVEL list that merely fetches differently.
 *   local  — the caller hands over `rows`. State lives in the caller's React
 *            state and NEVER touches the URL.
 *
 * The URL boundary is between `local` and the other two — not "remote has it,
 * the rest don't". A tab-embedded list writing its needle into the URL pollutes
 * share links and browser history and fights the parent list's own `search`.
 */
export const LIST_SOURCES = ['remote', 'bulk', 'local'] as const;
export type ListSource = (typeof LIST_SOURCES)[number];

/** Whether a source owns URL state. The single definition of that boundary. */
export function ownsUrlState(source: ListSource): boolean {
  return source !== 'local';
}

export const LIST_CAPABILITIES = [
  'search',
  'filter',
  'sort',
  'export',
  'print',
  'select',
  'rowActions',
] as const;
export type ListCapability = (typeof LIST_CAPABILITIES)[number];

/** What a view asks for. Anything omitted takes the source's default. */
export type ListCapabilities = Partial<Record<ListCapability, boolean>>;

/** What the controller resolved. Every capability answered, no `undefined`. */
export type ResolvedListCapabilities = Readonly<Record<ListCapability, boolean>>;

/**
 * The facts a source needs to decide its own defaults. All optional: a `local`
 * list declares none of them.
 */
export interface CapabilityContext {
  /**
   * Whether the backend list route implements free-text `search` — i.e. its
   * generated `x-searchable` whitelist is non-empty. `undefined` means the
   * resource never declared it, which for a `remote` list means NO.
   */
  searchable?: boolean;
  /** The backend filter whitelist. Empty/absent ⇒ every filter is dropped. */
  filterable?: FilterWhitelist;
  /** The backend sort whitelist. Empty/absent ⇒ a `remote` list sorts by
   * nothing (#2114); the in-memory sources ignore it entirely. */
  sortable?: SortWhitelist;
  /** Whether the view wired row selection. */
  selectable?: boolean;
  /** Whether the view declared any row actions. */
  hasRowActions?: boolean;
}

function hasWhitelistedFilters(filterable?: FilterWhitelist): boolean {
  return filterable != null && Object.keys(filterable).length > 0;
}

/**
 * The per-source default table (design §6.6).
 *
 * The one that matters is `remote.search`. Free-text `search` needs a non-empty
 * backend whitelist; without one the kernel answers 400 and the WHOLE list
 * errors out. So it defaults to **false**, explicitly, everywhere — this is
 * risk #1 on the design's own list ("flipped to true during migration ⇒
 * production lists 400 en masse"). In-memory search (`bulk` / `local`) cannot
 * 400 anything, so there it defaults to true.
 */
function sourceDefault(
  capability: ListCapability,
  source: ListSource,
  ctx: CapabilityContext,
): boolean {
  switch (capability) {
    case 'search':
      // `?? false` is load-bearing — see the note above. Never `?? true`.
      return source === 'remote' ? (ctx.searchable ?? false) : true;
    case 'filter':
      // A `remote` list can only push filters the backend whitelists;
      // with no whitelist every filter is dropped, so offering the
      // builder would be a lie. In-memory filtering is always legal.
      return source === 'remote' ? hasWhitelistedFilters(ctx.filterable) : true;
    case 'sort':
      // For a `remote` list, absent and EMPTY mean the same thing: the
      // route sorts by nothing. This used to read `?? 1`, so an absent
      // whitelist answered TRUE — the seam's "no declaration = no limit"
      // reading, which is the inverse of what the backend means by an
      // empty `x-sortable` (#2114). `filter` above has always been strict
      // here; this is the same rule.
      //
      // The in-memory sources keep the wide default deliberately: they
      // sort the fetched rows themselves, no whitelist applies, and
      // client-paginated resources declare none.
      return source === 'remote' ? (ctx.sortable?.length ?? 0) > 0 : true;
    case 'export':
      // A page-level list exports; an embedded `local` list is a fragment
      // of someone else's page and opts in explicitly if it wants a CSV.
      return source !== 'local';
    case 'print':
      // The one capability that defaults OFF for every source. A CSV
      // derives from `columns` losslessly and its failure mode is a
      // file nobody opens; a printed sheet is paper, ink and human
      // eyes, and no generic default layout is right for 30 different
      // resources. A list opts in once someone has looked at what it
      // prints.
      return false;
    case 'select':
      // Same boundary as `export`, for the same reason: a page-level
      // list offers the affordance, an embedded `local` list is a
      // fragment of someone else's page and opts in explicitly.
      //
      // This used to default off everywhere, because a selection's only
      // consumer was a bulk command and a list without one had nothing
      // to do with ticked rows. "Export selected" (and print) gave every
      // page-level list a use for a selection, so the old default now
      // withholds an affordance for a reason that no longer holds.
      return source === 'local' ? (ctx.selectable ?? false) : true;
    case 'rowActions':
      return ctx.hasRowActions ?? false;
  }
}

/**
 * Resolve the capability set for one list. Explicit `requested` values always
 * win — a view may turn a capability OFF that its source would default on, and
 * ON one it would default off (the caller then owns the consequences).
 */
export function resolveListCapabilities(
  source: ListSource,
  requested: ListCapabilities | undefined,
  ctx: CapabilityContext = {},
): ResolvedListCapabilities {
  const resolved = {} as Record<ListCapability, boolean>;
  for (const capability of LIST_CAPABILITIES) {
    resolved[capability] = requested?.[capability] ?? sourceDefault(capability, source, ctx);
  }
  return resolved;
}
